import moment, { Moment } from 'moment-timezone';
import { timezones } from "../constants/timezones";

/**
 * Converts a given UTC date to a specified timezone.
 * @param configDate The date in UTC format.
 * @param timezoneString The desired timezone.
 * @returns The date converted to the specified timezone
 */
const transformUTCtoTimezone = (configDate: string, timezoneString: string)  => {
    let date: string;

    const utcDate = moment.utc(configDate).format();
    const timezone = Object.keys(timezones).find(key => timezones[key] === timezoneString) || 'Europe/London';
    date = moment.tz(utcDate, timezone).format();

    return date
}

/**
 * Gets the current date in UTC format.
 * @returns The current date in UTC format.
 */
const getUTCDate = () => {
    const today = new Date();
    const utcDate = moment.utc(today).format();

    return utcDate;
}

/**
 * Checks if a given start date is in the future compared to the current date.
 * @param start_date The start date to check.
 * @param currentDate The current date to compare against.
 * @returns True if the start date is in the future, false otherwise.
 */
const isFutureDate = (start_date, currentDate) => {
    return moment.utc(start_date).isAfter(currentDate)
}

/**
 * Checks if a given end date is in the past compared to the current date.
 * @param end_date The end date to check.
 * @param currentDate The current date to compare against.
 * @returns True if the end date is in the past, false otherwise.
 */
const isPastDate = (end_date, currentDate) => {
    return moment.utc(end_date).isBefore(currentDate);
}

/**
 * Checks if a date is valid: both formatted date strings and timestamps.
 * @param date The date string or timestamp to validate.
 * @param format Optional format (defaults to 'YYYY-MM-DD HH:mm').
 *               Only used if the input is not a timestamp.
 * @returns True if the date is valid, false otherwise.
 */
const isValidDate = (date: string, format: string = 'YYYY-MM-DD HH:mm'): boolean => {
    if (typeof date === 'string' && date.trim() === '') {
        return false;
    }
    const timestamp = Number(date);
    if (!isNaN(timestamp)) {
        return moment(timestamp).isValid();
    }
    return moment(date, format, true).isValid();
};

/**
 * Formats a date according to the specified format.
 * @param date The date to format (string or Moment object).
 * @param format The desired format (defaults to YYYY-MM-DD HH:mm).
 * @returns The formatted date string.
 */
const formatDate = (date: string | moment.Moment, format: string = 'YYYY-MM-DD HH:mm') => {
    if (typeof date === 'string') {
      return moment.utc(date).format(format);
    }
    // If it's already a Moment object
    return date.format(format);
}

/**
 * Formats a UTC timestamp into a readable date string.
 * @param timestamp The UTC timestamp to format (in milliseconds).
 * @returns The formatted date string in ISO 8601 format.
 */
const formatUtcTimestampToDate = (timestamp: number): string => {
    return moment.utc(timestamp).format();
  };

/**
 * Converts a given date to a Moment object with an optional timezone.
 * @param date The date to convert (can be a string, Date object, or other valid input for moment).
 * @param timezone Optional timezone string.
 * @returns The converted Moment object.
 */
const convertToMoment = (date: string | Date | number | Moment | null | undefined, timezone?: string): Moment => {
    if (timezone) {
        return moment.tz(date, timezone);
    }
    return moment(date);
};

/**
 * Checks if two dates are not the same.
 * @param date1 The first date to compare.
 * @param date2 The second date to compare againts.
 * @returns True if the dates are not on the same calendar day, false otherwise.
 */
const isNotSameDate = (date1, date2) => {
    return !moment(date1).isSame(date2);
}

/**
 * Returns a Moment instance representing the start of day
 * for a date that is `days` days in the future.
 *
 * @param days  Number of days from today
 * @returns     Moment object at 00:00:00 of the target date
 */
const getFutureDate = (days: number) => {
    return moment().add(days, 'days').startOf('day');
}

/**
 * Creates date restrictions for date pickers based on provided start and end dates.
 * @param startDate Optional minimum date restriction.
 * @param endDate Optional maximum date restriction.
 * @returns Object containing minDate and maxDate as Moment objects or null.
 */
const createDateRestrictions = (
    startDate?: string | Moment,
    endDate?: string | Moment
): { minDate: Moment | null; maxDate: Moment | null } => {
    return {
        minDate: convertToMoment(startDate),
        maxDate: convertToMoment(endDate)
    };
};

/**
 * Compares two timestamps and determines whether the **date part only**
 * of the first timestamp is before the date part of the second timestamp.
 *
 * Time components (hours, minutes, seconds, milliseconds) are ignored.
 *
 * @param timestamp1 - The first timestamp (in milliseconds since Unix epoch).
 * @param timestamp2 - The second timestamp (in milliseconds since Unix epoch).
 * @returns `true` if the date of `timestamp1` is before the date of `timestamp2`,
 */
const isOnlyDateBefore = (timestamp1: number, timestamp2: number): boolean => {
    const d1 = new Date(timestamp1);
    const d2 = new Date(timestamp2);

    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);

    return d1.getTime() < d2.getTime();
};

export {
    isOnlyDateBefore,
    transformUTCtoTimezone,
    getUTCDate,
    isFutureDate,
    isPastDate,
    isValidDate,
    formatDate,
    formatUtcTimestampToDate,
    convertToMoment,
    isNotSameDate,
    getFutureDate,
    createDateRestrictions
};
